import { Fragment, useMemo, useState } from 'react'
import {
  type ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Info,
  Search,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import {
  PACKAGING_EPR_MATERIALS,
  useGetPackagingShipmentEpr,
  type PackagingEprMaterial,
  type PackagingShipmentEprComponent,
  type PackagingShipmentEprPackedItem,
  type PackagingShipmentEprWeights,
} from '@/shared/hooks/api/packagingShipments'
import { Button } from '@/components/ui/button'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { DataTablePagination } from '@/components/data-table'
import {
  groupEprByPackagingItem,
  packagingGroupMatchesQuery,
  type EprPackagingGroup,
} from '@/features/packaging-suppliers/shipmentEprByPackagingItem'

const ITEM_LINK_CLASS =
  'text-foreground underline decoration-dotted underline-offset-2'

type ShipmentEprPanelProps = {
  shipmentId: string
}

type EprTableView = 'packedItem' | 'packagingItem'

function packagingKey(packedItemId: string, packagingItemId: string): string {
  return `${packedItemId}:${packagingItemId}`
}

function expandablePackagingKeys(
  items: PackagingShipmentEprPackedItem[]
): string[] {
  const keys: string[] = []
  for (const item of items) {
    for (const packagingItem of item.packagingItems) {
      if (packagingItem.components.length === 0) {
        continue
      }
      keys.push(packagingKey(item.id, packagingItem.id))
    }
  }
  return keys
}

function sumWeights(
  parts: PackagingShipmentEprWeights[]
): PackagingShipmentEprWeights {
  const totals: PackagingShipmentEprWeights = {}
  for (const part of parts) {
    for (const material of PACKAGING_EPR_MATERIALS) {
      const grams = part[material]
      if (grams != null && grams > 0) {
        totals[material] = (totals[material] ?? 0) + grams
      }
    }
  }
  return totals
}

function filterPackedItems(
  items: PackagingShipmentEprPackedItem[],
  query: string
): PackagingShipmentEprPackedItem[] {
  const matched: PackagingShipmentEprPackedItem[] = []
  for (const item of items) {
    const packedMatches =
      item.sku.toLocaleLowerCase().includes(query) ||
      item.name.toLocaleLowerCase().includes(query)
    if (packedMatches) {
      matched.push(item)
      continue
    }
    const packagingItems = item.packagingItems.filter((packagingItem) =>
      packagingItem.name.toLocaleLowerCase().includes(query)
    )
    if (packagingItems.length === 0) {
      continue
    }
    matched.push({
      ...item,
      packagingItems,
      weights: sumWeights(
        packagingItems.map((packagingItem) => packagingItem.weights)
      ),
    })
  }
  return matched
}

function toggleMember(current: Set<string>, id: string): Set<string> {
  const next = new Set(current)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  return next
}

function positiveGrams(
  weights: PackagingShipmentEprWeights,
  material: PackagingEprMaterial
): number | null {
  const grams = weights[material]
  if (grams == null || !(grams > 0)) {
    return null
  }
  return grams
}

function ExpandButton({
  expanded,
  label,
  onClick,
}: {
  expanded: boolean
  label: string
  onClick: () => void
}) {
  const Icon = expanded ? ChevronDown : ChevronRight
  return (
    <button
      type='button'
      className='inline-flex size-6 shrink-0 items-center justify-center rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring'
      aria-expanded={expanded}
      aria-label={label}
      onClick={onClick}
    >
      <Icon className='size-4' aria-hidden />
    </button>
  )
}

export function ShipmentEprPanel({ shipmentId }: ShipmentEprPanelProps) {
  const { t, i18n } = useTranslation('packagingSuppliers')
  const epr = useGetPackagingShipmentEpr(shipmentId, true)
  const [searchInput, setSearchInput] = useState('')
  const [view, setView] = useState<EprTableView>('packedItem')
  const [expandedPackedIds, setExpandedPackedIds] = useState<Set<string>>(
    () => new Set()
  )
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(
    () => new Set()
  )
  const [expandedPackagingKeys, setExpandedPackagingKeys] = useState<
    Set<string>
  >(() => new Set())
  const cards = epr.data?.cards ?? []
  const loadedPackedItems = epr.data?.packedItems
  const materials = cards.map((card) => card.material)
  const query = searchInput.trim().toLocaleLowerCase()
  const packedItems = useMemo(() => {
    const source = loadedPackedItems ?? []
    if (query.length === 0 || view !== 'packedItem') {
      return source
    }
    return filterPackedItems(source, query)
  }, [loadedPackedItems, query, view])
  const packagingGroups = useMemo(() => {
    const groups = groupEprByPackagingItem(loadedPackedItems ?? [])
    if (query.length === 0 || view !== 'packagingItem') {
      return groups
    }
    return groups.filter((group) => packagingGroupMatchesQuery(group, query))
  }, [loadedPackedItems, query, view])
  const gramFormat = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 }),
    [i18n.language]
  )
  const formatGrams = (grams: number) =>
    t('shipmentsPage.result.epr.grams', { grams: gramFormat.format(grams) })
  const packagingKeys = expandablePackagingKeys(packedItems)
  const expandableGroupIds = packagingGroups
    .filter((group) => group.components.length > 0)
    .map((group) => group.id)
  const packedAllExpanded =
    packedItems.length > 0 &&
    packedItems.every((item) => expandedPackedIds.has(item.id)) &&
    packagingKeys.every((key) => expandedPackagingKeys.has(key))
  const packagingAllExpanded =
    expandableGroupIds.length > 0 &&
    expandableGroupIds.every((id) => expandedGroupIds.has(id))
  const allExpanded =
    view === 'packedItem' ? packedAllExpanded : packagingAllExpanded
  const showPackedView = view === 'packedItem'

  const showLoading = epr.isPending
  const showError = !epr.isPending && epr.isError
  const loaded = !epr.isPending && !epr.isError
  const showTable = loaded
  const packedItemCount = loadedPackedItems?.length ?? 0
  const activeCount = showPackedView
    ? packedItems.length
    : packagingGroups.length
  const showEmpty = loaded && packedItemCount === 0
  const showNoMatches = loaded && packedItemCount > 0 && activeCount === 0
  const showRows = loaded && activeCount > 0
  const showCards = loaded && cards.length > 0
  const showSearch = loaded && packedItemCount > 0
  const showExpandAll = showPackedView
    ? showRows
    : loaded && expandableGroupIds.length > 0
  const columnCount = (showPackedView ? 4 : 3) + materials.length

  const packedColumns = useMemo<ColumnDef<PackagingShipmentEprPackedItem>[]>(
    () => [{ accessorKey: 'id' }],
    []
  )
  const groupColumns = useMemo<ColumnDef<EprPackagingGroup>[]>(
    () => [{ accessorKey: 'id' }],
    []
  )
  const packedTable = useReactTable({
    data: packedItems,
    columns: packedColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    initialState: { pagination: { pageSize: 20 } },
  })
  const packagingTable = useReactTable({
    data: packagingGroups,
    columns: groupColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    initialState: { pagination: { pageSize: 20 } },
  })

  const expandLabel = (expanded: boolean, name: string) =>
    expanded
      ? t('shipmentsPage.result.epr.collapseItem', { name })
      : t('shipmentsPage.result.epr.expandItem', { name })

  return (
    <div className='flex flex-col gap-4'>
      {showLoading && (
        <p className='text-sm text-muted-foreground'>
          {t('shipmentsPage.result.epr.loading')}
        </p>
      )}
      {showError && (
        <p className='text-sm text-muted-foreground'>
          {t('shipmentsPage.result.epr.loadFailed')}
        </p>
      )}
      {showCards && (
        <dl className='grid shrink-0 grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5'>
          {cards.map((card) => (
            <div key={card.material}>
              <dt className='text-sm text-muted-foreground'>
                {t(`componentEditor.materials.${card.material}`)}
              </dt>
              <dd className='text-lg font-semibold'>
                {formatGrams(card.grams)}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {showSearch && (
        <div className='flex items-center gap-2'>
          <InputGroup className='h-8 w-full max-w-80'>
            <InputGroupAddon>
              <Search aria-hidden />
            </InputGroupAddon>
            <InputGroupInput
              value={searchInput}
              placeholder={t('shipmentsPage.result.epr.searchPlaceholder')}
              onChange={(event) => {
                const nextInput = event.target.value
                const nextQuery = nextInput.trim().toLocaleLowerCase()
                const source = loadedPackedItems ?? []
                setSearchInput(nextInput)
                packedTable.setPageIndex(0)
                packagingTable.setPageIndex(0)
                if (nextQuery.length === 0) {
                  setExpandedPackedIds(new Set())
                  setExpandedPackagingKeys(new Set())
                  setExpandedGroupIds(new Set())
                  return
                }
                if (view === 'packedItem') {
                  const matched = filterPackedItems(source, nextQuery)
                  setExpandedPackedIds(new Set(matched.map((item) => item.id)))
                  setExpandedPackagingKeys(
                    new Set(expandablePackagingKeys(matched))
                  )
                  return
                }
                const matchedGroups = groupEprByPackagingItem(source).filter(
                  (group) => packagingGroupMatchesQuery(group, nextQuery)
                )
                setExpandedGroupIds(
                  new Set(
                    matchedGroups
                      .filter((group) => group.components.length > 0)
                      .map((group) => group.id)
                  )
                )
              }}
            />
          </InputGroup>
          <span className='text-sm text-muted-foreground'>
            {t('shipmentsPage.result.epr.viewBy')}
          </span>
          <Tabs
            value={view}
            onValueChange={(value) => {
              if (value !== 'packedItem' && value !== 'packagingItem') {
                return
              }
              setView(value)
              packedTable.setPageIndex(0)
              packagingTable.setPageIndex(0)
              setExpandedPackedIds(new Set())
              setExpandedPackagingKeys(new Set())
              setExpandedGroupIds(new Set())
            }}
          >
            <TabsList className='h-8'>
              <TabsTrigger value='packedItem'>
                {t('shipmentsPage.result.epr.viewPackedItem')}
              </TabsTrigger>
              <TabsTrigger value='packagingItem'>
                {t('shipmentsPage.result.epr.viewPackagingItem')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {showExpandAll && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              className='ms-auto'
              onClick={() => {
                if (allExpanded) {
                  setExpandedPackedIds(new Set())
                  setExpandedPackagingKeys(new Set())
                  setExpandedGroupIds(new Set())
                  return
                }
                if (view === 'packedItem') {
                  setExpandedPackedIds(
                    new Set(packedItems.map((item) => item.id))
                  )
                  setExpandedPackagingKeys(new Set(packagingKeys))
                  return
                }
                setExpandedGroupIds(new Set(expandableGroupIds))
              }}
            >
              {allExpanded ? (
                <ChevronsDownUp aria-hidden />
              ) : (
                <ChevronsUpDown aria-hidden />
              )}
              {allExpanded
                ? t('shipmentsPage.result.collapseAll')
                : t('shipmentsPage.result.expandAll')}
            </Button>
          )}
        </div>
      )}
      {showTable && (
        <div className='flex h-[32rem] flex-col overflow-hidden rounded-md border'>
          <div className='min-h-0 flex-1 overflow-auto'>
            <table className='w-max min-w-full caption-bottom text-sm'>
              <TableHeader>
                <TableRow>
                  {showPackedView ? (
                    <>
                      <TableHead className='sticky top-0 left-0 z-20 bg-background'>
                        {t('shipmentsPage.result.columns.sku')}
                      </TableHead>
                      <TableHead className='sticky top-0 z-10 w-44 bg-background'>
                        {t('shipmentsPage.result.columns.packedItem')}
                      </TableHead>
                      <TableHead className='sticky top-0 z-10 bg-background'>
                        {t('shipmentsPage.result.columns.packagingItem')}
                      </TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead className='sticky top-0 left-0 z-20 bg-background'>
                        {t('shipmentsPage.result.columns.packagingItem')}
                      </TableHead>
                      <TableHead className='sticky top-0 z-10 bg-background'>
                        {t('shipmentsPage.result.epr.usedByColumn')}
                      </TableHead>
                    </>
                  )}
                  <TableHead className='sticky top-0 z-10 bg-background'>
                    {t('shipmentsPage.result.epr.component')}
                  </TableHead>
                  {materials.map((material) => (
                    <TableHead
                      key={material}
                      className='sticky top-0 z-10 bg-background text-end'
                    >
                      {t(`componentEditor.materials.${material}`)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {showEmpty && (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount}
                      className='h-24 text-center text-muted-foreground'
                    >
                      {t('shipmentsPage.result.epr.empty')}
                    </TableCell>
                  </TableRow>
                )}
                {showNoMatches && (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount}
                      className='h-24 text-center text-muted-foreground'
                    >
                      {t('shipmentsPage.result.epr.noMatches')}
                    </TableCell>
                  </TableRow>
                )}
                {showRows &&
                  showPackedView &&
                  packedTable.getRowModel().rows.map((pageItem) => {
                    const item = pageItem.original
                    const packedExpanded = expandedPackedIds.has(item.id)
                    return (
                      <Fragment key={item.id}>
                        <TableRow>
                          <TableCell className='sticky left-0 z-10 bg-background'>
                            <span className='flex items-center gap-1'>
                              <ExpandButton
                                expanded={packedExpanded}
                                label={expandLabel(packedExpanded, item.name)}
                                onClick={() =>
                                  setExpandedPackedIds((current) =>
                                    toggleMember(current, item.id)
                                  )
                                }
                              />
                              <span
                                className='block max-w-40 truncate'
                                title={item.sku}
                              >
                                {item.sku}
                              </span>
                            </span>
                          </TableCell>
                          <TableCell className='w-44 font-medium'>
                            <Link
                              className={`block max-w-32 truncate ${ITEM_LINK_CLASS}`}
                              title={item.name}
                              to={`/packaging/packed-items/${item.id}`}
                            >
                              {item.name}
                            </Link>
                          </TableCell>
                          <TableCell />
                          <TableCell />
                          {materials.map((material) => (
                            <TableCell
                              key={material}
                              className='text-end font-medium tabular-nums'
                            >
                              {formatWeight(
                                positiveGrams(item.weights, material),
                                formatGrams
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                        {packedExpanded &&
                          item.packagingItems.map((packagingItem) => {
                            const key = packagingKey(item.id, packagingItem.id)
                            const hasComponents =
                              packagingItem.components.length > 0
                            const packagingExpanded =
                              expandedPackagingKeys.has(key)
                            return (
                              <Fragment key={key}>
                                <TableRow className='bg-muted/30'>
                                  <TableCell className='sticky left-0 z-10 bg-muted' />
                                  <TableCell />
                                  <TableCell>
                                    <span className='flex items-center gap-1'>
                                      <span className='inline-flex size-6 shrink-0 items-center justify-center'>
                                        {hasComponents && (
                                          <ExpandButton
                                            expanded={packagingExpanded}
                                            label={expandLabel(
                                              packagingExpanded,
                                              packagingItem.name
                                            )}
                                            onClick={() =>
                                              setExpandedPackagingKeys(
                                                (current) =>
                                                  toggleMember(current, key)
                                              )
                                            }
                                          />
                                        )}
                                      </span>
                                      <Link
                                        className={`block max-w-56 truncate ${ITEM_LINK_CLASS}`}
                                        title={packagingItem.name}
                                        to={`/packaging/suppliers/${packagingItem.supplierId}/items/${packagingItem.id}`}
                                      >
                                        {packagingItem.name}
                                      </Link>
                                    </span>
                                  </TableCell>
                                  <TableCell />
                                  {materials.map((material) => (
                                    <TableCell
                                      key={material}
                                      className='text-end tabular-nums'
                                    >
                                      {formatWeight(
                                        positiveGrams(
                                          packagingItem.weights,
                                          material
                                        ),
                                        formatGrams
                                      )}
                                    </TableCell>
                                  ))}
                                </TableRow>
                                {hasComponents &&
                                  packagingExpanded &&
                                  packagingItem.components.map(
                                    (component, index) => (
                                      <ComponentRow
                                        key={`${key}:${index}`}
                                        component={component}
                                        materials={materials}
                                        formatGrams={formatGrams}
                                      />
                                    )
                                  )}
                              </Fragment>
                            )
                          })}
                      </Fragment>
                    )
                  })}
                {showRows && !showPackedView && (
                  <PackagingItemEprRows
                    groups={packagingTable
                      .getRowModel()
                      .rows.map((row) => row.original)}
                    materials={materials}
                    expandedGroupIds={expandedGroupIds}
                    formatGrams={formatGrams}
                    perUnitLabel={(grams) =>
                      t('shipmentsPage.result.epr.perUnit', {
                        grams: gramFormat.format(grams),
                      })
                    }
                    expandLabel={expandLabel}
                    usedByLabel={(count) =>
                      t('shipmentsPage.result.epr.usedBy', { count })
                    }
                    usedByTitle={t('shipmentsPage.result.epr.usedByTitle')}
                    onToggleGroup={(id) =>
                      setExpandedGroupIds((current) =>
                        toggleMember(current, id)
                      )
                    }
                  />
                )}
              </TableBody>
            </table>
          </div>
          {showPackedView ? (
            <DataTablePagination
              table={packedTable}
              className='shrink-0 border-t bg-background py-3'
            />
          ) : (
            <DataTablePagination
              table={packagingTable}
              className='shrink-0 border-t bg-background py-3'
            />
          )}
        </div>
      )}
    </div>
  )
}

function formatWeight(
  grams: number | null,
  formatGrams: (grams: number) => string
): string | null {
  if (grams == null) {
    return null
  }
  return formatGrams(grams)
}

function ComponentRow({
  component,
  materials,
  formatGrams,
}: {
  component: PackagingShipmentEprComponent
  materials: PackagingEprMaterial[]
  formatGrams: (grams: number) => string
}) {
  return (
    <TableRow className='bg-muted/30'>
      <TableCell className='sticky left-0 z-10 bg-muted' />
      <TableCell />
      <TableCell />
      <TableCell>{component.name}</TableCell>
      {materials.map((material) => {
        const grams = component.material === material ? component.grams : null
        let label: string | null = null
        if (grams != null && grams > 0) {
          label = formatGrams(grams)
        }
        return (
          <TableCell key={material} className='text-end tabular-nums'>
            {label}
          </TableCell>
        )
      })}
    </TableRow>
  )
}

function PackagingItemEprRows({
  groups,
  materials,
  expandedGroupIds,
  formatGrams,
  perUnitLabel,
  expandLabel,
  usedByLabel,
  usedByTitle,
  onToggleGroup,
}: {
  groups: EprPackagingGroup[]
  materials: PackagingEprMaterial[]
  expandedGroupIds: Set<string>
  formatGrams: (grams: number) => string
  perUnitLabel: (grams: number) => string
  expandLabel: (expanded: boolean, name: string) => string
  usedByLabel: (count: number) => string
  usedByTitle: string
  onToggleGroup: (id: string) => void
}) {
  return groups.map((group) => {
    const expanded = expandedGroupIds.has(group.id)
    const hasComponents = group.components.length > 0
    return (
      <Fragment key={group.id}>
        <TableRow>
          <TableCell className='sticky left-0 z-10 bg-background font-medium'>
            <span className='flex items-center gap-1'>
              <span className='inline-flex size-6 shrink-0 items-center justify-center'>
                {hasComponents && (
                  <ExpandButton
                    expanded={expanded}
                    label={expandLabel(expanded, group.name)}
                    onClick={() => onToggleGroup(group.id)}
                  />
                )}
              </span>
              <Link
                className={`block max-w-56 truncate ${ITEM_LINK_CLASS}`}
                title={group.name}
                to={`/packaging/suppliers/${group.supplierId}/items/${group.id}`}
              >
                {group.name}
              </Link>
            </span>
          </TableCell>
          <TableCell>
            <UsedByButton
              group={group}
              label={usedByLabel(group.users.length)}
              title={usedByTitle}
            />
          </TableCell>
          <TableCell />
          {materials.map((material) => (
            <TableCell
              key={material}
              className='text-end font-medium tabular-nums'
            >
              {formatWeight(
                positiveGrams(group.weights, material),
                formatGrams
              )}
            </TableCell>
          ))}
        </TableRow>
        {hasComponents &&
          expanded &&
          group.components.map((component, index) => (
            <TableRow key={`${group.id}:${index}`} className='bg-muted/30'>
              <TableCell className='sticky left-0 z-10 bg-muted' />
              <TableCell />
              <TableCell>{component.name}</TableCell>
              {materials.map((material) => {
                const showComponent = component.material === material
                const grams = showComponent ? component.grams : null
                const unitGrams = showComponent ? component.unitGrams : null
                return (
                  <TableCell key={material} className='text-end tabular-nums'>
                    <ComponentShipmentWeight
                      grams={grams}
                      unitGrams={unitGrams}
                      formatGrams={formatGrams}
                      perUnitLabel={perUnitLabel}
                    />
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
      </Fragment>
    )
  })
}

function UsedByButton({
  group,
  label,
  title,
}: {
  group: EprPackagingGroup
  label: string
  title: string
}) {
  return (
    <Popover>
      <PopoverTrigger
        className={`inline-flex items-center gap-1 ${ITEM_LINK_CLASS}`}
      >
        {label}
        <Info className='size-3.5' aria-hidden />
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='flex max-h-(--available-height) w-72 flex-col overflow-hidden p-2'
      >
        <p className='shrink-0 px-2 py-1 text-sm font-medium'>{title}</p>
        <ul className='flex min-h-0 flex-col overflow-y-auto'>
          {group.users.map((user) => (
            <li key={user.packedItemId}>
              <Link
                className='flex flex-col rounded-sm px-2 py-1 hover:bg-muted'
                to={`/packaging/packed-items/${user.packedItemId}`}
              >
                <span>{user.packedItemName}</span>
                <span className='text-xs text-muted-foreground'>
                  {user.sku}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}

function ComponentShipmentWeight({
  grams,
  unitGrams,
  formatGrams,
  perUnitLabel,
}: {
  grams: number | null
  unitGrams: number | null
  formatGrams: (grams: number) => string
  perUnitLabel: (grams: number) => string
}) {
  const label = formatWeight(grams, formatGrams)
  if (label == null || unitGrams == null || !(unitGrams > 0)) {
    return label
  }
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span className='cursor-default underline decoration-dotted underline-offset-2' />
        }
      >
        {label}
      </TooltipTrigger>
      <TooltipContent>{perUnitLabel(unitGrams)}</TooltipContent>
    </Tooltip>
  )
}
